import { DeleteOutline } from "@mui/icons-material";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import DiscountIcon from "@mui/icons-material/Discount";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useCompany } from "../../components/context/CompanyContext";
import Loading from "../../components/loading/loading";
import "../../global.css";
import { userRequest } from "../../requestMethods";
import "./productList.css";
import Avatar from "@mui/material/Avatar";
import { generateFileUrl } from "../../requestMethods";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import { useDispatch, useSelector } from "react-redux";
import { titleSetRef } from "../../redux/titleRedux";
import { setAllFilters } from "../../redux/customGridRedux";
import CustomGrid from "../../components/grid/CustomGrid";
import { fullPriceForList } from "../../util/productListPrice";

export default function ProductList() {
  const [searchParams] = useSearchParams();
  const currentUser = useSelector((state) => state.user.currentUser);
  const allFilters = useSelector(
    (state) => state.customGrid["productList"]?.allFilters || [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const { companyId, slug } = useCompany();
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRows, setTotalRows] = useState(0);
  const dispatch = useDispatch();
  // Parâmetros sintéticos de URL (filtros de dashboard) — persistem entre trocas de página
  const syntheticRef = useRef({});

  const salesStart = searchParams.get("start");
  const salesEnd = searchParams.get("end");
  const noSales = searchParams.get("noSales");
  const withSales = searchParams.get("withSales");
  const salesDir = searchParams.get("salesDir");
  const refFilter = searchParams.get("ref");
  const presalesFilter = searchParams.get("presales");
  const stockFilter = searchParams.get("stockFilter");
  const storeIdParam = searchParams.get("storeId"); // loja(s) vindas do indicador
  const from = searchParams.get("from");

  useEffect(() => {
    dispatch(titleSetRef({ title: "Produtos" }));
    if (companyId > 0) {
      loadProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, salesStart, salesEnd, noSales, withSales, salesDir, refFilter, presalesFilter, stockFilter, storeIdParam]);

  // Busca unidades vendidas por referência no período selecionado
  const getSalesByRef = async () => {
    try {
      const filters = [];
      if (salesStart) filters.push({ field: "createdAt", operator: "greaterThanOrEqual", value: salesStart });
      if (salesEnd) filters.push({ field: "createdAt", operator: "lessThanOrEqual", value: salesEnd });

      const res = await userRequest.post(`${companyId}/orders/find`, {
        page: 0,
        limit: 10000,
        paginated: true,
        filters,
        ...(!currentUser?.isAdmin && { stories: [currentUser?.storeId] }),
      });
      const list = res.data.rows || [];
      const map = {};
      list.forEach((o) => {
        (o.products || []).forEach((p) => {
          if (p.ref) map[p.ref] = (map[p.ref] || 0) + (p.quantity || 1);
        });
      });
      return map;
    } catch {
      return {};
    }
  };

  // Monta os parâmetros sintéticos a partir dos searchParams e chama getProducts(1)
  const loadProducts = async () => {
    // Só "Com venda" precisa do mapa de vendas (ordenação + coluna salesCount).
    // "Sem venda" é resolvido no backend (modo noSales).
    const needsSalesMap = withSales;
    const salesMap = needsSalesMap ? await getSalesByRef() : {};

    const synthetic = { salesMap, salesDir };

    if (noSales) {
      // Produtos visíveis sem venda no período — backend calcula os refs vendidos
      // (mesma fonte do card "Sem Venda") e aplica visibilidade calculada.
      synthetic.noSalesStart = salesStart;
      synthetic.noSalesEnd = salesEnd;
    }

    if (withSales) {
      const refsWithSales = Object.keys(salesMap);
      if (refsWithSales.length === 0) {
        setData([]);
        setTotalRows(0);
        return;
      }
      // Ordena as refs por unidades vendidas no período (desc = mais vendidos,
      // asc = menos vendidos) e pede ao servidor para paginar NESSA ordem.
      // Sem isso, "mais" e "menos" mostravam os mesmos produtos (só reordenava a página).
      const dir = salesDir === "asc" ? 1 : -1;
      const sortedRefs = [...refsWithSales].sort(
        (a, b) => dir * ((salesMap[a] || 0) - (salesMap[b] || 0)),
      );
      synthetic.refsIn = sortedRefs;
      synthetic.orderByRefs = true;
      synthetic.withSales = true;
    }

    if (presalesFilter) synthetic.presalesFilter = true;
    if (stockFilter) synthetic.stockFilter = stockFilter;
    if (storeIdParam) {
      synthetic.stories = storeIdParam.split(",").map((s) => s.trim()).filter(Boolean);
    }

    // Veio do indicador (há parâmetro sintético na URL)? Então sincroniza a barra
    // de filtros do grid para não exibir o filtro de coluna antigo (persistido).
    // "ref" vira filtro de Referência (visível/editável); os demais sintéticos
    // (withSales/noSales/stock/presales) não têm coluna, então a barra fica limpa.
    const cameFromIndicator =
      salesStart || salesEnd || noSales || withSales || refFilter || presalesFilter || stockFilter;
    const gridFilters = refFilter
      ? [{ field: "ref", operator: "equals", value: refFilter }]
      : [];
    if (cameFromIndicator) {
      dispatch(setAllFilters({ gridId: "productList", filters: gridFilters }));
    }

    syntheticRef.current = synthetic;
    await getProducts(1, pageSize, cameFromIndicator ? gridFilters : null, synthetic);
  };

  // Corpo do /products/admin/find — o mesmo para a página da tela e para exportar.
  const buildProductsBody = (newPage, newPageSize, filtersToUse, syn, sortOverride) => {
    const activeFilters = filtersToUse !== null ? filtersToUse : allFilters;
    return {
      page: newPage,
      limit: newPageSize,
      filters: activeFilters,
      ...(sortOverride && { sort: sortOverride }),
      ...(syn.stockFilter && { stockFilter: syn.stockFilter }),
      ...(syn.presalesFilter && { presalesFilter: true }),
      ...(syn.refFilter && { refFilter: syn.refFilter }),
      ...(syn.refsIn && { refsIn: syn.refsIn }),
      ...(syn.orderByRefs && { orderByRefs: true }),
      ...(syn.stories && syn.stories.length && { stories: syn.stories }),
      ...(syn.noSalesStart && { noSalesStart: syn.noSalesStart }),
      ...(syn.noSalesEnd && { noSalesEnd: syn.noSalesEnd }),
      ...(syn.refsNotIn && { refsNotIn: syn.refsNotIn }),
      ...(syn.enabledFilter !== undefined && { enabledFilter: syn.enabledFilter }),
    };
  };

  const mapProductRows = (list, syn) => {
    const sm = syn.salesMap || {};
    let rows = (list || []).map((r) => ({
      ...r,
      discount: fullPriceForList(r),
      salesCount: sm[r.ref] || 0,
    }));

    // Ordena por salesCount client-side quando vem do dashboard "Com venda" + salesDir
    if (syn.withSales && syn.salesDir) {
      rows = syn.salesDir === "asc"
        ? rows.sort((a, b) => a.salesCount - b.salesCount)
        : rows.sort((a, b) => b.salesCount - a.salesCount);
    }
    return rows;
  };

  // Excel/PDF: todos os produtos com os mesmos filtros (inclusive os sintéticos
  // do dashboard) e a ordenação atual do grid.
  const fetchExportRows = async ({ sort }) => {
    const syn = syntheticRef.current;
    const body = {
      ...buildProductsBody(1, 25000, null, syn, sort),
      paginated: true,
      exportMode: true,
    };
    const res = await userRequest.post(`${companyId}/products/admin/find`, body);
    return mapProductRows(res.data?.rows, syn);
  };

  const getProducts = async (
    newPage = page,
    newPageSize = pageSize,
    filtersToUse = null,
    syntheticOverride = null,
    sortOverride = null,
  ) => {
    try {
      setIsLoading(true);
      const syn = syntheticOverride !== null ? syntheticOverride : syntheticRef.current;
      const body = buildProductsBody(newPage, newPageSize, filtersToUse, syn, sortOverride);

      const res = await userRequest.post(`${companyId}/products/admin/find`, body);
      const rows = mapProductRows(res.data.rows, syn);

      setData(rows);
      setTotalRows(res.data.totalRecords);
      setPage(newPage);
      setPageSize(newPageSize);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
    }
  };

  const deletePrd = async (prd) => {
    const res = await userRequest.delete(
      `${companyId}/products/${prd}&${currentUser.userId}`,
    );

    if (res.data > 0) {
      getProducts();
    } else {
      Swal.fire("Atenção!", "Erro ao excluir produto!", "error");
    }
  };

  const updatePrd = async (productId) => {
    const res = await userRequest.put(`${companyId}/products/${productId}`, {
      updateImages: new Date(),
    });

    if (res.data > 0) {
      getProducts();
    } else {
      Swal.fire("Atenção!", "Erro ao atualizar produto!", "error");
    }
  };

  const onButtonDeleteClick = (e, row) => {
    if (currentUser.isAdmin) {
      Swal.fire({
        title: "Tem certeza que deseja excluir?",
        text: "Você não poderá reverter isso!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#EE8253",
        confirmButtonText: "Sim, excluir!",
        cancelButtonColor: "#d33",
        cancelButtonText: "Cancelar",
      }).then(async (result) => {
        if (result.isConfirmed) {
          deletePrd(row);
        }
      });
    } else {
      Swal.fire(
        "Atenção!",
        "Você não tem permissão para excluir o produto!",
        "warning",
      );
    }
  };

  const onButtonRefreshClick = (e, row) => {
    if (currentUser.isAdmin) {
      Swal.fire({
        title: "Tem certeza que deseja que as fotos apareçam no topo?",
        text: "Você não poderá reverter isso!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#EE8253",
        confirmButtonText: "Sim, atualizar!",
        cancelButtonColor: "#d33",
        cancelButtonText: "Cancelar",
      }).then(async (result) => {
        if (result.isConfirmed) {
          updatePrd(row);
        }
      });
    } else {
      Swal.fire(
        "Atenção!",
        "Você não tem permissão para atualizar o produto!",
        "warning",
      );
    }
  };

  const columns = [
    {
      field: "salesCount",
      type: "number",
      headerName: "Vendas",
      width: 90,
      align: "center",
      headerAlign: "center",
      description: salesStart
        ? "Unidades vendidas no período"
        : "Unidades vendidas (total)",
      renderCell: (params) => (
        <span style={{ fontWeight: 700, color: "#10B981" }}>
          {params.value || 0}
        </span>
      ),
    },
    {
      field: "avatar",
      headerName: "Foto",
      align: "center",
      headerAlign: "center",
      width: 60,
      filterable: false,
      clickable: true,
      renderCell: (params) => {
        return (
          <>
            <Avatar
              sx={{ width: "30px", height: "30px" }}
              src={generateFileUrl(params.value)}
            />
          </>
        );
      },
    },
    {
      field: "upImages",
      headerName: "Atualizar foto",
      width: 130,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <IconButton
            onClick={(e) => onButtonRefreshClick(e, params.row.productId)}
            variant="contained"
          >
            <RefreshIcon />
          </IconButton>
        );
      },
    },
    {
      field: "edit",
      headerName: "Editar",
      align: "center",
      headerAlign: "center",
      width: 80,
      filterable: false,
      clickable: true,
      renderCell: (params) => {
        return (
          <IconButton
            onClick={(e) => nav(`${params.row.productId}`)}
            variant="contained"
          >
            <EditIcon />
          </IconButton>
        );
      },
    },
    {
      field: "presales",
      type: "boolean",
      headerName: "Pré-venda",
      filterable: false,
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <input
            className="star"
            type="checkbox"
            name="presales"
            checked={params.value ? true : false}
          />
        );
      },
    },
    {
      field: "enabled",
      headerName: "Visível",
      type: "boolean",
      width: 60,
      align: "center",
      headerAlign: "center",
      description: "Visível = true e Não visível = falso",
      renderCell: (params) => {
        if (params.value) {
          return <VisibilityOutlinedIcon fontSize="small" color="success" />;
        } else {
          return (
            <VisibilityOffOutlinedIcon fontSize="small" sx={{ color: "red" }} />
          );
        }
      },
    },
    {
      field: "status",
      type: "text",
      headerName: "Status Produto",
      width: 120,
      align: "center",
      headerAlign: "center",
      description:
        "S = todas numeração tem em estoque e tem foto, P = alguma numeração não tem em estoque e tem foto, N = não tem estoque ou não tem foto",
      renderCell: (params) => {
        // eslint-disable-next-line default-case
        switch (params.value) {
          case "S":
            return (
              <>
                <CheckCircleOutlineOutlinedIcon color="success" />
              </>
            );
          case "N":
            return (
              <>
                <RemoveCircleOutlineOutlinedIcon sx={{ color: "red" }} />
              </>
            );
          case "P":
            return (
              <>
                <ReportGmailerrorredIcon sx={{ color: "#EE8253" }} />
              </>
            );
        }
      },
    },
    {
      field: "ref",
      type: "text",
      headerName: "Referência",
      width: 100,
      align: "center",
    },
    {
      field: "title",
      type: "text",
      align: "left",
      headerName: "Produto",
      width: 250,
      clickable: true,
    },
    {
      field: "category",
      type: "text",
      headerName: "Categoria",
      width: 100,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "subCategory",
      type: "text",
      headerName: "Sub Categoria",
      width: 100,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "discount",
      type: "number",
      headerName: "Preço",
      width: 80,
      align: "center",
      headerAlign: "center",
      format: "BRL",
    },
    {
      field: "percentDiscount",
      type: "number",
      headerName: "% Desconto",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "price",
      type: "number",
      headerName: "Preço com Desconto",
      width: 160,
      align: "center",
      headerAlign: "center",
      format: "BRL",
    },
    {
      field: "promo",
      type: "boolean",
      headerName: "Promocional",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return params.value ? <DiscountIcon color="success" /> : false;
      },
    },
    {
      field: "countImages",
      type: "number",
      headerName: "Qtde de Fotos",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "stock",
      type: "text",
      headerName: "Estoque",
      width: 80,
      align: "center",
      headerAlign: "center",
      description:
        "S = todas numeração tem em estoque, P = alguma numeração não tem em estoque, N = não tem estoque",
      renderCell: (params) => {
        // eslint-disable-next-line default-case
        switch (params.value) {
          case "S":
            return (
              <>
                <CheckCircleOutlineOutlinedIcon color="success" />
              </>
            );
          case "N":
            return (
              <>
                <RemoveCircleOutlineOutlinedIcon sx={{ color: "red" }} />
              </>
            );
          case "P":
            return (
              <>
                <ReportGmailerrorredIcon sx={{ color: "#EE8253" }} />
              </>
            );
        }
      },
    },
    {
      field: "qtdeStock",
      type: "number",
      headerName: "Qtde Prod.",
      width: 60,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "createdAt",
      type: "date",
      headerName: "Criada em",
      width: 100,
      align: "center",
      headerAlign: "center",
      format: "dd/MM/yyyy",
    },
    {
      field: "updatedAt",
      type: "date",
      headerName: "Atualizada em",
      width: 100,
      align: "center",
      headerAlign: "center",
      format: "dd/MM/yyyy",
    },
    {
      field: "visibleAt",
      type: "date",
      headerName: "Visível em",
      width: 100,
      align: "center",
      headerAlign: "center",
      format: "dd/MM/yyyy",
    },
    {
      // Data do pedido mais recente (não cancelado) com o produto — calculada na API.
      field: "lastSaleAt",
      type: "date",
      headerName: "Última venda",
      width: 100,
      align: "center",
      headerAlign: "center",
      format: "dd/MM/yyyy",
    },
    {
      field: "updateImages",
      type: "date",
      headerName: "Foto atualizada em",
      width: 120,
      align: "center",
      headerAlign: "center",
      format: "dd/MM/yyyy",
    },
    {
      field: "delete",
      headerName: "",
      width: 80,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (currentUser.isAdmin) {
          return (
            <IconButton
              onClick={(e) => onButtonDeleteClick(e, params.row.productId)}
              variant="contained"
            >
              <DeleteOutline />
            </IconButton>
          );
        }
      },
    },
  ];

  const nav = useNavigate();
  const addRow = () => {
    nav(`new`);
  };

  const handlePageChangeServer = (newPage, newPageSize, extra) => {
    getProducts(newPage + 1, newPageSize, null, null, extra?.sort ?? null);
  };

  return (
    <div className="cust-page">
      {from && (
        <div
          onClick={() => nav(`/${slug}/${from}`)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 12,
            cursor: "pointer",
            color: "#6B7280",
            fontSize: 13,
            fontWeight: 600,
            fontFamily: "Manrope",
            padding: "6px 12px",
            borderRadius: 8,
            border: "1px solid #E5E7EB",
            background: "#fff",
            boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
          }}
        >
          ← Voltar para Indicadores
        </div>
      )}
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <CustomGrid
            toolbarActions={
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={addRow}
                size="small"
              >
                Adicionar Produto
              </Button>
            }
            gridId="productList"
            rows={data}
            columns={columns}
            pageSize={pageSize}
            serverSide={true}
            rowCount={totalRows}
            onPageChangeServer={handlePageChangeServer}
            checkboxSelection={false}
            endpopintExport="/products/admin/find"
            callExport="post"
            fetchExportRows={fetchExportRows}
            tableHeight="calc(100vh - 110px)"
          />
        </>
      )}
    </div>
  );
}
